import { useEffect, useMemo, useState } from 'react';
import {
  BadgeCheck,
  Banknote,
  ClipboardCheck,
  FileCheck2,
  PackageOpen,
  Pencil,
  Plus,
  Printer,
  RotateCcw,
  Search,
  Send,
  Truck,
  X,
  XCircle
} from 'lucide-react';
import {
  markProcurementPurchaseOrderOrdered,
  payProcurementSupplierInvoice,
  postProcurementGoodsReceipt,
  postProcurementSupplierReturn,
  receiveProcurementSupplierRefund,
  loadProcurementPurchaseOrderPrint,
  recordProcurementSupplierInvoice,
  reviewProcurementPurchaseOrder,
  saveProcurementPurchaseOrder,
  saveProcurementSupplier,
  loadBhpRequests,
  loadBhpItemsAll,
  updateBhpRequestStatus
} from './data.js';
import { dateLabel, friendlyError, jakartaDateKey, number, rupiah } from './lib/format.js';

const TABS = [
  { key: 'summary', label: 'Ringkasan' },
  { key: 'bhp_requests', label: 'Permintaan Internal' },
  { key: 'bhp_inventory', label: 'Inventaris BHP' },
  { key: 'suppliers', label: 'Supplier' },
  { key: 'orders', label: 'Purchase Order' },
  { key: 'receipts', label: 'Penerimaan Barang' },
  { key: 'finance', label: 'Invoice & Pembayaran' },
  { key: 'returns', label: 'Retur Supplier' }
];

const BHP_STATUS_COLOR = {
  REQUESTED: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  DIPROSES:  { bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
  DIKIRIM:   { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  DITERIMA:  { bg: '#f0fdf4', color: '#15803d', border: '#86efac' },
};

const STATUS_LABELS = {
  DRAFT: 'Draft',
  WAITING_APPROVAL: 'Menunggu Approval',
  APPROVED: 'Disetujui',
  ORDERED: 'Dikirim ke Supplier',
  RECEIVED: 'Barang Diterima',
  CLOSED: 'Selesai',
  POSTED: 'Diposting',
  MATCHED: 'Cocok',
  VARIANCE: 'Selisih',
  VERIFIED: 'Terverifikasi',
  ON_HOLD: 'Ditahan',
  PARTIALLY_PAID: 'Dibayar Sebagian',
  PAID: 'Lunas',
  SETTLED: 'Lunas setelah Retur',
  PENDING: 'Menunggu Penyesuaian',
  PENDING_INVOICE: 'Menunggu Invoice',
  APPLIED_TO_PAYABLE: 'Mengurangi Utang',
  REFUND_DUE: 'Refund Belum Diterima',
  REFUND_PARTIAL: 'Refund Sebagian',
  NOT_RECEIVED: 'Belum Diterima',
  PARTIALLY_RECEIVED: 'Diterima Sebagian',
  FULLY_RECEIVED: 'Diterima Lengkap'
};

function statusLabel(value) {
  const key = String(value || '').toUpperCase();
  return STATUS_LABELS[key] || value || '—';
}

function tone(value) {
  const text = String(value || '').toUpperCase();
  if (/ACTIVE|POSTED|CLOSED|FULLY|MATCHED|PAID|VERIFIED|RECEIVED|SETTLED|APPLIED_TO_PAYABLE/.test(text)) return 'positive';
  if (/INACTIVE|CANCEL/.test(text)) return 'danger';
  if (/PARTIAL|DRAFT|ORDERED|APPROVED|WAITING|VARIANCE|ON_HOLD|PENDING|REFUND_DUE/.test(text)) return 'warning';
  return 'neutral';
}

function Pill({ children }) {
  return <span className={`status status-${tone(children)}`}>{statusLabel(children)}</span>;
}

function Empty({ children }) {
  return <div className="procurement-empty"><PackageOpen size={30} /><p>{children}</p></div>;
}

function Metric({ label, value, note, icon, toneName = '' }) {
  const Icon = icon;
  return (
    <article className={`metric ${toneName ? `metric-${toneName}` : ''}`}>
      <div className="metric-icon"><Icon size={21} /></div>
      <div><span>{label}</span><strong>{value}</strong><small>{note}</small></div>
    </article>
  );
}

function Modal({ title, subtitle, icon, children, actions, onClose, wide = false, className = '' }) {
  const Icon = icon;
  return (
    <div className="modal-backdrop" role="presentation">
      <section className={`modal-card procurement-modal ${wide ? 'procurement-modal-wide' : ''} ${className}`} role="dialog" aria-modal="true">
        <header className="modal-head">
          <div className="modal-title-icon"><Icon size={22} /></div>
          <div className="grow"><h2>{title}</h2><p>{subtitle}</p></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Tutup"><X size={20} /></button>
        </header>
        <div className="modal-body procurement-modal-body">{children}</div>
        <footer className="modal-actions">{actions}</footer>
      </section>
    </div>
  );
}

function SupplierForm({ supplier, busy, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    id: supplier?.id || '',
    supplier_code: supplier?.supplier_code || '',
    supplier_name: supplier?.supplier_name || '',
    contact_person: supplier?.contact_person || '',
    phone: supplier?.phone || '',
    email: supplier?.email || '',
    address: supplier?.address || '',
    payment_terms_days: supplier?.payment_terms_days ?? 0,
    status: supplier?.status || 'ACTIVE',
    notes: supplier?.notes || ''
  }));

  const field = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  return (
    <Modal
      title={supplier ? 'Edit Supplier' : 'Supplier Baru'}
      subtitle="Master supplier tenant XV. Setiap perubahan dicatat pada audit log."
      icon={Truck}
      onClose={onClose}
      actions={<>
        <button className="button button-quiet" type="button" onClick={onClose}>Batal</button>
        <button className="button button-primary" type="button" disabled={busy || !form.supplier_name.trim()} onClick={() => onSaved(form)}>
          {busy ? 'Menyimpan…' : 'Simpan Supplier'}
        </button>
      </>}
    >
      <div className="form-grid">
        <label className="form-field">Kode supplier
          <input value={form.supplier_code} onChange={field('supplier_code')} placeholder="Otomatis bila dikosongkan" maxLength={40} />
        </label>
        <label className="form-field">Nama supplier *
          <input value={form.supplier_name} onChange={field('supplier_name')} maxLength={200} required />
        </label>
        <label className="form-field">Kontak person
          <input value={form.contact_person} onChange={field('contact_person')} maxLength={150} />
        </label>
        <label className="form-field">Telepon
          <input value={form.phone} onChange={field('phone')} maxLength={50} />
        </label>
        <label className="form-field">Email
          <input type="email" value={form.email} onChange={field('email')} maxLength={200} />
        </label>
        <label className="form-field">Termin pembayaran
          <div className="input-suffix"><input type="number" min="0" max="3650" value={form.payment_terms_days} onChange={field('payment_terms_days')} /><span>hari</span></div>
        </label>
        <label className="form-field">Status
          <select value={form.status} onChange={field('status')}><option value="ACTIVE">Aktif</option><option value="INACTIVE">Tidak aktif</option></select>
        </label>
        <label className="form-field form-field-wide">Alamat
          <textarea rows="2" value={form.address} onChange={field('address')} maxLength={1000} />
        </label>
        <label className="form-field form-field-wide">Catatan
          <textarea rows="2" value={form.notes} onChange={field('notes')} maxLength={2000} />
        </label>
      </div>
    </Modal>
  );
}

function blankOrderLine(items, preferredItem) {
  const selected = preferredItem?.id ? (items.find((item) => item.id === preferredItem.id) || preferredItem) : items[0];
  const gudangStock = Number(preferredItem?.gudang_stock ?? selected?.gudang_stock ?? selected?.current_stock ?? 0);
  const targetStock = Number(preferredItem?.target_stock ?? selected?.target_stock ?? 0);
  const suggestedQty = targetStock > gudangStock ? targetStock - gudangStock : 1;
  return {
    bhp_item_id: selected?.id || '',
    quantity_ordered: suggestedQty,
    unit_cost: selected?.last_purchase_price || selected?.average_cost || 0,
    notes: preferredItem?.id ? 'Purchase stok gudang di bawah limit.' : ''
  };
}

function PurchaseOrderForm({ order, suppliers, items, initialItem, initialItems = [], busy, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    id: order?.id || '',
    supplier_id: order?.supplier_id || suppliers.find((row) => row.status === 'ACTIVE')?.id || '',
    po_date: order?.po_date || jakartaDateKey(),
    expected_date: order?.expected_date || '',
    discount_amount: order?.discount_amount ?? 0,
    notes: order?.notes || ''
  }));
  const [lines, setLines] = useState(() => order?.items?.length
    ? order.items.map((row) => ({
        bhp_item_id: row.bhp_item_id,
        quantity_ordered: row.quantity_ordered,
        unit_cost: row.unit_cost,
        notes: row.notes || ''
      }))
    : initialItems.length
      ? initialItems.map((item) => blankOrderLine(items, item))
      : [blankOrderLine(items, initialItem)]);

  const subtotal = lines.reduce((sum, row) => sum + Number(row.quantity_ordered || 0) * Number(row.unit_cost || 0), 0);
  const total = Math.max(0, subtotal - Number(form.discount_amount || 0));
  const field = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const lineField = (index, key) => (event) => {
    const value = event.target.value;
    setLines((current) => current.map((line, lineIndex) => {
      if (lineIndex !== index) return line;
      if (key !== 'bhp_item_id') return { ...line, [key]: value };
      const selected = items.find((item) => item.id === value);
      return { ...line, bhp_item_id: value, unit_cost: selected?.last_purchase_price || selected?.average_cost || line.unit_cost || 0 };
    }));
  };
  const canSave = form.supplier_id && lines.length > 0 && lines.every((row) => row.bhp_item_id && Number(row.quantity_ordered) > 0 && Number(row.unit_cost) >= 0);

  return (
    <Modal
      title={order ? `Edit ${order.po_number}` : 'Purchase Order Baru'}
      subtitle="Simpan sebagai draft untuk koreksi, lalu ajukan ke Keuangan. PO belum dapat dicetak sebelum disetujui."
      icon={ClipboardCheck}
      onClose={onClose}
      wide
      actions={<>
        <button className="button button-quiet" type="button" onClick={onClose}>Batal</button>
        <button className="button button-soft" type="button" disabled={busy || !canSave} onClick={() => onSaved(form, lines, false)}>Simpan Draft</button>
        <button className="button button-primary" type="button" disabled={busy || !canSave} onClick={() => onSaved(form, lines, true)}><Send size={17} /> Ajukan ke Keuangan</button>
      </>}
    >
      <div className="form-grid">
        <label className="form-field">Supplier *
          <select value={form.supplier_id} onChange={field('supplier_id')} required>
            <option value="">Pilih supplier</option>
            {suppliers.filter((row) => row.status === 'ACTIVE').map((row) => <option key={row.id} value={row.id}>{row.supplier_code} · {row.supplier_name}</option>)}
          </select>
        </label>
        <label className="form-field">Tanggal PO *<input type="date" value={form.po_date} onChange={field('po_date')} required /></label>
        <label className="form-field">Estimasi diterima<input type="date" min={form.po_date} value={form.expected_date} onChange={field('expected_date')} /></label>
        <label className="form-field">Diskon nilai<input type="number" min="0" step="1" value={form.discount_amount} onChange={field('discount_amount')} /></label>
        <label className="form-field form-field-wide">Catatan<textarea rows="2" value={form.notes} onChange={field('notes')} maxLength={2000} /></label>
      </div>

      <section className="procurement-lines">
        <div className="procurement-lines-head"><div><span className="eyebrow">Item PO</span><h3>Barang yang dipesan</h3></div><button className="button button-mini button-soft" type="button" onClick={() => setLines((current) => [...current, blankOrderLine(items)])}><Plus size={14} /> Tambah item</button></div>
        {lines.map((line, index) => (
          <div className="procurement-line" key={`${index}-${line.bhp_item_id}`}>
            <label className="form-field procurement-line-item">Item
              <select value={line.bhp_item_id} onChange={lineField(index, 'bhp_item_id')}>
                <option value="">Pilih item</option>
                {items.map((item) => <option key={item.id} value={item.id}>{item.item_code} · {item.name} · stok {number.format(item.current_stock)} {item.unit}</option>)}
              </select>
            </label>
            <label className="form-field">Jumlah<input type="number" min="0.0001" step="0.0001" value={line.quantity_ordered} onChange={lineField(index, 'quantity_ordered')} /></label>
            <label className="form-field">Harga satuan<input type="number" min="0" step="1" value={line.unit_cost} onChange={lineField(index, 'unit_cost')} /></label>
            <div className="procurement-line-total"><span>Subtotal</span><strong>{rupiah.format(Number(line.quantity_ordered || 0) * Number(line.unit_cost || 0))}</strong></div>
            <button className="icon-button procurement-remove" type="button" disabled={lines.length === 1} onClick={() => setLines((current) => current.filter((_, lineIndex) => lineIndex !== index))}><X size={17} /></button>
          </div>
        ))}
      </section>
      <div className="procurement-totals"><span>Subtotal <strong>{rupiah.format(subtotal)}</strong></span><span>Diskon <strong>{rupiah.format(Number(form.discount_amount || 0))}</strong></span><span className="procurement-grand-total">Total PO <strong>{rupiah.format(total)}</strong></span></div>
    </Modal>
  );
}

function contactValue(contact, keys) {
  for (const key of keys) {
    if (contact?.[key]) return contact[key];
  }
  return '';
}

function PurchaseOrderPrintModal({ data, canApprove, canOrder, canReceive, onClose, onReview, onOrder, onReceive }) {
  const { clinic, supplier, purchase_order: order, items = [] } = data;
  const contact = clinic.contact || {};
  const clinicAddress = contactValue(contact, ['clinic_address', 'address', 'alamat']);
  const clinicPhone = contactValue(contact, ['clinic_phone', 'phone', 'telephone']);
  const clinicEmail = contactValue(contact, ['email']);
  const footer = contactValue(contact, ['purchase_order_footer', 'po_footer']);
  const orderStatus = String(order.status || '').toUpperCase();
  const canPrint = ['APPROVED', 'ORDERED', 'RECEIVED', 'CLOSED'].includes(orderStatus);
  const openForReceipt = orderStatus === 'ORDERED';

  return (
    <Modal
      title="Purchase Order"
      subtitle={`${order.po_number} · ${supplier.supplier_name} · ${order.status}`}
      icon={ClipboardCheck}
      onClose={onClose}
      wide
      className="modal-card-po-print"
      actions={<>
        <button className="button button-quiet" type="button" onClick={onClose}>Tutup</button>
        {canApprove && orderStatus === 'WAITING_APPROVAL' ? <button className="button button-soft" type="button" onClick={onReview}><BadgeCheck size={17} /> Review Keuangan</button> : null}
        {canPrint ? <button className="button button-soft" type="button" onClick={() => window.print()}><Printer size={17} /> Cetak PO</button> : null}
        {canOrder && orderStatus === 'APPROVED' ? <button className="button button-primary" type="button" onClick={onOrder}><Send size={17} /> Tandai Dikirim</button> : null}
        {canReceive && openForReceipt ? <button className="button button-primary" type="button" onClick={onReceive}><PackageOpen size={17} /> Terima Barang</button> : null}
      </>}
    >
      {orderStatus === 'WAITING_APPROVAL' ? <div className="alert alert-warning"><strong>Menunggu persetujuan Keuangan.</strong> Dokumen dapat diperiksa, tetapi tombol cetak baru aktif setelah disetujui.</div> : null}
      {orderStatus === 'DRAFT' ? <div className="alert alert-info"><strong>Draft internal.</strong> Lengkapi atau koreksi PO kemudian ajukan kembali ke Keuangan.</div> : null}
      {orderStatus === 'APPROVED' ? <div className="alert alert-success"><strong>Disetujui Keuangan.</strong> PO sudah boleh dicetak dan dikirim kepada supplier.</div> : null}
      <article className="purchase-order-print">
        <header className="po-print-head">
          <div>
            <span>{clinic.code}{clinic.slogan || contactValue(contact, ['slogan']) ? ` · ${clinic.slogan || contactValue(contact, ['slogan'])}` : ''}</span>
            <h2>{clinic.legal_name || clinic.name}</h2>
            {clinicAddress ? <p>{clinicAddress}</p> : null}
            {[clinicPhone, clinicEmail].filter(Boolean).length ? <p>{[clinicPhone, clinicEmail].filter(Boolean).join(' · ')}</p> : null}
          </div>
          <div className="po-print-number">
            <span>PURCHASE ORDER</span>
            <strong>{order.po_number}</strong>
            <small>{dateLabel(order.po_date)}</small>
          </div>
        </header>

        <section className="po-print-parties">
          <div><span>Supplier</span><strong>{supplier.supplier_name}</strong><small>{supplier.supplier_code}</small><small>{supplier.address || 'Alamat belum diisi'}</small></div>
          <div><span>Kontak supplier</span><strong>{supplier.contact_person || '—'}</strong><small>{[supplier.phone, supplier.email].filter(Boolean).join(' · ') || '—'}</small><small>Termin {supplier.payment_terms_days || 0} hari</small></div>
          <div><span>Status dokumen</span><strong>{statusLabel(order.status)}</strong><small>Estimasi diterima: {dateLabel(order.expected_date)}</small><small>{statusLabel(order.fulfillment_status)}</small></div>
        </section>

        {!canPrint ? <div className="po-draft-mark">DOKUMEN INTERNAL · BELUM DISETUJUI KEUANGAN</div> : null}

        <table className="invoice-items po-print-items">
          <thead><tr><th>No.</th><th>Kode / Item</th><th>Jumlah</th><th>Harga Satuan</th><th>Subtotal</th></tr></thead>
          <tbody>{items.map((item, index) => <tr key={item.id}><td>{index + 1}</td><td><strong>{item.item_name}</strong><small>{item.item_code}{item.notes ? ` · ${item.notes}` : ''}</small></td><td>{number.format(item.quantity_ordered)} {item.unit}</td><td>{rupiah.format(item.unit_cost)}</td><td>{rupiah.format(item.subtotal)}</td></tr>)}</tbody>
        </table>

        <div className="invoice-totals">
          <div><span>Subtotal</span><strong>{rupiah.format(order.subtotal)}</strong></div>
          <div><span>Diskon</span><strong>{rupiah.format(order.discount_amount)}</strong></div>
          <div className="invoice-total-main"><span>Total PO</span><strong>{rupiah.format(order.total_amount)}</strong></div>
        </div>

        {order.notes ? <div className="po-print-notes"><span>Catatan</span><p>{order.notes}</p></div> : null}
        {order.rejection_reason ? <div className="po-print-notes po-rejection-note"><span>Alasan revisi terakhir</span><p>{order.rejection_reason}</p></div> : null}
        <section className="po-print-signatures">
          <div><span>Dibuat oleh</span><strong>{data.prepared_by || '—'}</strong></div>
          <div><span>Diajukan oleh</span><strong>{data.submitted_by || 'Belum diajukan'}</strong></div>
          <div><span>Disetujui Keuangan</span><strong>{data.approved_by || 'Belum disetujui'}</strong></div>
          <div><span>Dikirim oleh</span><strong>{data.ordered_by || 'Belum dikirim'}</strong></div>
          <div><span>Konfirmasi supplier</span><strong>________________</strong></div>
        </section>
        {footer ? <p className="invoice-footer-note">{footer}</p> : null}
      </article>
    </Modal>
  );
}

function PurchaseOrderReviewModal({ order, busy, onClose, onDecision }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      title="Review Purchase Order"
      subtitle={`${order.po_number} · ${order.supplier_name} · ${rupiah.format(order.total_amount)}`}
      icon={BadgeCheck}
      onClose={onClose}
      actions={<>
        <button className="button button-quiet" type="button" onClick={onClose}>Batal</button>
        <button className="button button-danger" type="button" disabled={busy || reason.trim().length < 5} onClick={() => onDecision('REJECT', reason)}><XCircle size={17} /> Kembalikan untuk Revisi</button>
        <button className="button button-primary" type="button" disabled={busy} onClick={() => onDecision('APPROVE', '')}><BadgeCheck size={17} /> Setujui PO</button>
      </>}
    >
      <div className="alert alert-info">Persetujuan membuka fitur cetak PO. Penerimaan barang tetap baru dapat dilakukan setelah PO ditandai telah dikirim ke supplier.</div>
      <label className="form-field">Alasan bila dikembalikan
        <textarea rows="4" value={reason} onChange={(event) => setReason(event.target.value)} maxLength={1000} placeholder="Contoh: harga perlu dikonfirmasi atau jumlah harus dikoreksi." />
        <small>Wajib minimal 5 karakter hanya bila PO dikembalikan.</small>
      </label>
    </Modal>
  );
}

function GoodsReceiptForm({ orders, initialOrder, busy, onClose, onSaved }) {
  const openOrders = orders.filter((row) => String(row.status).toUpperCase() === 'ORDERED' && row.items?.some((item) => Number(item.remaining_quantity) > 0));
  const [poId, setPoId] = useState(openOrders.some((row) => row.id === initialOrder?.id) ? initialOrder.id : openOrders[0]?.id || '');
  const [form, setForm] = useState({
    receipt_date: jakartaDateKey(),
    supplier_delivery_number: '',
    invoice_number: '',
    invoice_date: jakartaDateKey(),
    invoice_amount: '',
    inspection_notes: '',
    notes: ''
  });
  const order = openOrders.find((row) => row.id === poId);
  const [quantities, setQuantities] = useState({});
  const [batches, setBatches] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [invoiceAmountTouched, setInvoiceAmountTouched] = useState(false);
  const lines = (order?.items || []).filter((row) => Number(row.remaining_quantity) > 0).map((row) => ({
    po_item_id: row.id,
    quantity_received: Number(quantities[row.id] || 0),
    unit_cost: Number(row.unit_cost || 0),
    batch_number: batches[row.id]?.batch_number || '',
    expiry_date: batches[row.id]?.expiry_date || ''
  }));
  const selected = lines.filter((row) => row.quantity_received > 0);
  const selectedTotal = selected.reduce((sum, row) => sum + Number(row.quantity_received || 0) * Number(row.unit_cost || 0), 0);
  const invoiceAmount = Number(form.invoice_amount || 0);
  const invoiceComplete = form.invoice_number.trim() && form.invoice_date && form.invoice_amount !== '' && invoiceAmount >= 0;
  const invoiceMatched = invoiceComplete && Math.abs(invoiceAmount - selectedTotal) <= 0.5;
  useEffect(() => {
    if (invoiceAmountTouched) return;
    const nextAmount = selectedTotal > 0 ? String(selectedTotal) : '';
    setForm((current) => current.invoice_amount === nextAmount
      ? current
      : { ...current, invoice_amount: nextAmount });
  }, [selectedTotal, invoiceAmountTouched]);
  const field = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const invoiceAmountField = (event) => {
    setInvoiceAmountTouched(true);
    setForm((current) => ({ ...current, invoice_amount: event.target.value }));
  };
  const getValidationMessage = () => {
    const missing = [];
    if (!poId) missing.push('Purchase Order');
    if (selected.length === 0) missing.push('jumlah barang diterima minimal satu item');
    if (!form.invoice_number.trim()) missing.push('nomor invoice');
    if (!form.invoice_date) missing.push('tanggal invoice');
    if (form.invoice_amount === '') {
      missing.push(`nilai invoice (isi ${rupiah.format(selectedTotal)} bila sesuai total barang)`);
    } else if (!Number.isFinite(invoiceAmount) || invoiceAmount < 0) {
      missing.push('nilai invoice yang valid');
    }
    return missing.length ? `Lengkapi ${missing.join(', ')}.` : '';
  };
  const validationMessage = submitAttempted ? getValidationMessage() : '';
  const handleSubmit = () => {
    setSubmitAttempted(true);
    if (getValidationMessage()) return;
    onSaved({ ...form, po_id: poId }, selected);
  };

  return (
    <Modal
      title="Posting Penerimaan Barang"
      subtitle="Posting bersifat final: stok dan kartu stok langsung bertambah secara atomik."
      icon={PackageOpen}
      onClose={onClose}
      wide
      actions={<>
        <button className="button button-quiet" type="button" onClick={onClose}>Batal</button>
        <button className="button button-primary" type="button" disabled={busy} onClick={handleSubmit}>{busy ? 'Memposting…' : 'Posting Penerimaan & Invoice'}</button>
      </>}
    >
      <div className="alert alert-warning"><strong>Final.</strong> Setelah berhasil, dokumen tidak dapat diedit dari portal dan stok otomatis bertambah.</div>
      <div className="form-grid">
        <label className="form-field form-field-wide">Purchase Order *
          <select value={poId} onChange={(event) => { setPoId(event.target.value); setQuantities({}); setBatches({}); setInvoiceAmountTouched(false); setForm((current) => ({ ...current, invoice_amount: '' })); }}>
            <option value="">Pilih PO terbuka</option>
            {openOrders.map((row) => <option key={row.id} value={row.id}>{row.po_number} · {row.supplier_name} · {rupiah.format(row.total_amount)}</option>)}
          </select>
        </label>
          <label className="form-field">Tanggal penerimaan *<input type="date" required max={jakartaDateKey()} value={form.receipt_date} onChange={field('receipt_date')} /></label>
        <label className="form-field">Nomor surat jalan<input value={form.supplier_delivery_number} onChange={field('supplier_delivery_number')} maxLength={100} /></label>
        <label className="form-field form-field-wide">Catatan<textarea rows="2" value={form.notes} onChange={field('notes')} maxLength={2000} /></label>
      </div>
      {!order ? <Empty>Tidak ada PO terbuka untuk diterima.</Empty> : (
        <section className="procurement-lines">
          <div className="procurement-lines-head"><div><span className="eyebrow">Item penerimaan</span><h3>{order.po_number} · {order.supplier_name}</h3></div></div>
          {order.items.filter((row) => Number(row.remaining_quantity) > 0).map((row) => (
            <div className="receipt-line" key={row.id}>
              <div className="primary-cell"><strong>{row.item_code} · {row.item_name}</strong><span>Sisa PO {number.format(row.remaining_quantity)} {row.unit} · {rupiah.format(row.unit_cost)}/{row.unit}</span></div>
              <label className="form-field">Diterima
                <input type="number" min="0" max={row.remaining_quantity} step="0.0001" value={quantities[row.id] || ''} onChange={(event) => setQuantities((current) => ({ ...current, [row.id]: event.target.value }))} placeholder="0" />
              </label>
              <label className="form-field">Batch
                <input value={batches[row.id]?.batch_number || ''} onChange={(event) => setBatches((current) => ({ ...current, [row.id]: { ...current[row.id], batch_number: event.target.value } }))} />
              </label>
              <label className="form-field">Kedaluwarsa
                <input type="date" min={form.receipt_date} value={batches[row.id]?.expiry_date || ''} onChange={(event) => setBatches((current) => ({ ...current, [row.id]: { ...current[row.id], expiry_date: event.target.value } }))} />
              </label>
            </div>
          ))}
        </section>
      )}
      <section className="procurement-invoice-entry">
        <div className="procurement-lines-head"><div><span className="eyebrow">Invoice supplier</span><h3>Dokumen yang diterima bersama barang</h3></div><strong>{rupiah.format(selectedTotal)}</strong></div>
        <div className="form-grid">
          <label className="form-field">Nomor invoice *<input required value={form.invoice_number} onChange={field('invoice_number')} maxLength={100} /></label>
          <label className="form-field">Tanggal invoice *<input required type="date" max={jakartaDateKey()} value={form.invoice_date} onChange={field('invoice_date')} /></label>
          <label className="form-field">Nilai invoice *<input required type="number" min="0" step="1" value={form.invoice_amount} onChange={invoiceAmountField} placeholder={String(selectedTotal)} /><small>Otomatis mengikuti total barang diterima: {rupiah.format(selectedTotal)}. Tetap bisa diubah bila invoice berbeda.</small></label>
          <label className="form-field"><span>Kesesuaian</span><div className={`invoice-match-box ${invoiceMatched ? 'matched' : invoiceComplete ? 'variance' : ''}`}><strong>{invoiceComplete ? (invoiceMatched ? 'COCOK' : 'SELISIH') : 'Menunggu nilai'}</strong><small>Barang {rupiah.format(selectedTotal)} · Invoice {rupiah.format(invoiceAmount)}</small></div></label>
          <label className="form-field form-field-wide">Catatan pemeriksaan gudang<textarea rows="2" value={form.inspection_notes} onChange={field('inspection_notes')} maxLength={2000} placeholder="Kondisi fisik, kemasan, jumlah, atau perbedaan yang ditemukan." /></label>
        </div>
        {invoiceComplete && !invoiceMatched ? <div className="alert alert-warning"><strong>Invoice akan berstatus Ditahan.</strong> Keuangan tidak dapat membayar sebelum nilai invoice sesuai dengan barang yang diterima.</div> : null}
        {validationMessage ? <div className="alert alert-warning"><strong>Belum dapat diposting.</strong> {validationMessage}</div> : null}
      </section>
    </Modal>
  );
}

function SupplierInvoiceForm({ receipt, busy, onClose, onSaved }) {
  const [form, setForm] = useState({
    invoice_number: '',
    invoice_date: receipt.receipt_date || jakartaDateKey(),
    invoice_amount: receipt.total_amount ?? '',
    inspection_notes: ''
  });
  const field = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const matched = Math.abs(Number(form.invoice_amount || 0) - Number(receipt.total_amount || 0)) <= 0.5;
  const complete = form.invoice_number.trim() && form.invoice_date && form.invoice_amount !== '';
  return (
    <Modal
      title="Catat Invoice Supplier"
      subtitle={`${receipt.receipt_number} · ${receipt.supplier_name} · penerimaan ${rupiah.format(receipt.total_amount)}`}
      icon={FileCheck2}
      onClose={onClose}
      actions={<>
        <button className="button button-quiet" type="button" onClick={onClose}>Batal</button>
        <button className="button button-primary" type="button" disabled={busy || !complete} onClick={() => onSaved(receipt.id, form)}>{busy ? 'Menyimpan…' : 'Simpan & Verifikasi'}</button>
      </>}
    >
      <div className="form-grid">
        <label className="form-field">Nomor invoice *<input value={form.invoice_number} onChange={field('invoice_number')} maxLength={100} /></label>
        <label className="form-field">Tanggal invoice *<input type="date" max={jakartaDateKey()} value={form.invoice_date} onChange={field('invoice_date')} /></label>
        <label className="form-field">Nilai invoice *<input type="number" min="0" step="1" value={form.invoice_amount} onChange={field('invoice_amount')} /></label>
        <label className="form-field"><span>Kesesuaian</span><div className={`invoice-match-box ${matched ? 'matched' : 'variance'}`}><strong>{matched ? 'COCOK' : 'SELISIH'}</strong><small>Penerimaan {rupiah.format(receipt.total_amount)}</small></div></label>
        <label className="form-field form-field-wide">Catatan pemeriksaan gudang<textarea rows="3" value={form.inspection_notes} onChange={field('inspection_notes')} maxLength={2000} /></label>
      </div>
      {!matched ? <div className="alert alert-warning">Invoice disimpan dengan status <strong>Ditahan</strong> dan belum dapat dibayar.</div> : null}
    </Modal>
  );
}

function SupplierPaymentForm({ invoice, busy, onClose, onSaved }) {
  const outstanding = Number(invoice.outstanding_amount || 0);
  const [form, setForm] = useState({
    payment_date: jakartaDateKey(),
    amount: outstanding,
    payment_method: 'BANK_TRANSFER',
    payment_reference: '',
    notes: ''
  });
  const field = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const amount = Number(form.amount || 0);
  const canPay = amount > 0 && amount <= outstanding && form.payment_date && form.payment_method;
  return (
    <Modal
      title="Pembayaran Invoice Supplier"
      subtitle={`${invoice.invoice_number} · ${invoice.supplier_name}`}
      icon={Banknote}
      onClose={onClose}
      actions={<>
        <button className="button button-quiet" type="button" onClick={onClose}>Batal</button>
        <button className="button button-primary" type="button" disabled={busy || !canPay} onClick={() => onSaved(invoice.id, form)}>{busy ? 'Memposting…' : 'Posting Pembayaran'}</button>
      </>}
    >
      <div className="procurement-payment-summary">
        <div><span>Nilai invoice</span><strong>{rupiah.format(invoice.invoice_amount)}</strong></div>
        <div><span>Sudah dibayar</span><strong>{rupiah.format(invoice.paid_amount)}</strong></div>
        <div><span>Sisa invoice</span><strong>{rupiah.format(outstanding)}</strong></div>
      </div>
      <div className="form-grid">
        <label className="form-field">Tanggal pembayaran *<input type="date" max={jakartaDateKey()} value={form.payment_date} onChange={field('payment_date')} /></label>
        <label className="form-field">Jumlah pembayaran *<input type="number" min="1" max={outstanding} step="1" value={form.amount} onChange={field('amount')} /></label>
        <label className="form-field">Metode pembayaran *
          <select value={form.payment_method} onChange={field('payment_method')}>
            <option value="BANK_TRANSFER">Transfer bank</option><option value="CASH">Tunai</option><option value="QRIS">QRIS</option><option value="DEBIT_CARD">Kartu debit</option><option value="CREDIT_CARD">Kartu kredit</option><option value="OTHER">Lainnya</option>
          </select>
        </label>
        <label className="form-field">Referensi pembayaran<input value={form.payment_reference} onChange={field('payment_reference')} maxLength={200} placeholder="Nomor transfer / bukti bayar" /></label>
        <label className="form-field form-field-wide">Catatan<textarea rows="3" value={form.notes} onChange={field('notes')} maxLength={2000} /></label>
      </div>
      {amount < outstanding && amount > 0 ? <div className="alert alert-info">Pembayaran sebagian. Sisa {rupiah.format(outstanding - amount)} tetap tercatat sebagai utang supplier.</div> : null}
    </Modal>
  );
}

function SupplierRefundForm({ supplierReturn, busy, onClose, onSaved }) {
  const outstanding = Math.max(
    Number(supplierReturn.refund_due_amount || 0) - Number(supplierReturn.refunded_amount || 0),
    0
  );
  const [form, setForm] = useState({
    refund_date: jakartaDateKey(),
    amount: outstanding,
    refund_method: 'BANK_TRANSFER',
    refund_reference: '',
    notes: ''
  });
  const field = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const amount = Number(form.amount || 0);
  const canReceive = amount > 0 && amount <= outstanding && form.refund_date && form.refund_method;

  return (
    <Modal
      title="Terima Refund Supplier"
      subtitle={`${supplierReturn.return_number} · ${supplierReturn.supplier_name}`}
      icon={Banknote}
      onClose={onClose}
      actions={<>
        <button className="button button-quiet" type="button" onClick={onClose}>Batal</button>
        <button className="button button-primary" type="button" disabled={busy || !canReceive} onClick={() => onSaved(supplierReturn.id, form)}>{busy ? 'Memposting…' : 'Posting Refund'}</button>
      </>}
    >
      <div className="procurement-payment-summary">
        <div><span>Nilai retur</span><strong>{rupiah.format(supplierReturn.total_amount)}</strong></div>
        <div><span>Mengurangi utang</span><strong>{rupiah.format(supplierReturn.applied_to_invoice_amount)}</strong></div>
        <div><span>Sisa refund</span><strong>{rupiah.format(outstanding)}</strong></div>
      </div>
      <div className="alert alert-info">Dicatat oleh Keuangan setelah dana refund benar-benar diterima dari supplier.</div>
      <div className="form-grid">
        <label className="form-field">Tanggal diterima *<input type="date" min={supplierReturn.return_date || ''} max={jakartaDateKey()} value={form.refund_date} onChange={field('refund_date')} /></label>
        <label className="form-field">Jumlah refund *<input type="number" min="1" max={outstanding} step="1" value={form.amount} onChange={field('amount')} /></label>
        <label className="form-field">Metode penerimaan *
          <select value={form.refund_method} onChange={field('refund_method')}>
            <option value="BANK_TRANSFER">Transfer bank</option><option value="CASH">Tunai</option><option value="QRIS">QRIS</option><option value="OTHER">Lainnya</option>
          </select>
        </label>
        <label className="form-field">Referensi refund<input value={form.refund_reference} onChange={field('refund_reference')} maxLength={200} placeholder="Nomor transfer / bukti refund" /></label>
        <label className="form-field form-field-wide">Catatan<textarea rows="3" value={form.notes} onChange={field('notes')} maxLength={2000} /></label>
      </div>
      {amount < outstanding && amount > 0 ? <div className="alert alert-info">Refund sebagian. Sisa {rupiah.format(outstanding - amount)} tetap ditagih kepada supplier.</div> : null}
    </Modal>
  );
}

function SupplierReturnForm({ receipts, busy, onClose, onSaved }) {
  const eligibleReceipts = receipts.filter((receipt) => receipt.status === 'POSTED' && receipt.items?.some((item) => Number(item.quantity_received) > Number(item.quantity_already_returned || 0)));
  const [receiptId, setReceiptId] = useState(eligibleReceipts[0]?.id || '');
  const [form, setForm] = useState({ return_date: jakartaDateKey(), notes: '' });
  const [quantities, setQuantities] = useState({});
  const [lineNotes, setLineNotes] = useState({});
  const receipt = eligibleReceipts.find((row) => row.id === receiptId);
  const lines = (receipt?.items || []).map((row) => ({
    receipt_item_id: row.id,
    quantity_returned: Number(quantities[row.id] || 0),
    notes: lineNotes[row.id] || ''
  })).filter((row) => row.quantity_returned > 0);
  const field = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  return (
    <Modal
      title="Posting Retur Supplier"
      subtitle="Posting bersifat final: stok dan kartu stok langsung berkurang secara atomik."
      icon={RotateCcw}
      onClose={onClose}
      wide
      actions={<>
        <button className="button button-quiet" type="button" onClick={onClose}>Batal</button>
        <button className="button button-primary" type="button" disabled={busy || !receiptId || lines.length === 0} onClick={() => onSaved({ ...form, receipt_id: receiptId }, lines)}>{busy ? 'Memposting…' : 'Posting Retur'}</button>
      </>}
    >
      <div className="alert alert-warning"><strong>Final.</strong> Sistem menolak retur yang melebihi jumlah diterima atau stok yang tersedia.</div>
      <div className="form-grid">
        <label className="form-field form-field-wide">Dokumen penerimaan *
          <select value={receiptId} onChange={(event) => { setReceiptId(event.target.value); setQuantities({}); setLineNotes({}); }}>
            <option value="">Pilih penerimaan</option>
            {eligibleReceipts.map((row) => <option key={row.id} value={row.id}>{row.receipt_number} · {row.supplier_name} · {dateLabel(row.receipt_date)}</option>)}
          </select>
        </label>
        <label className="form-field">Tanggal retur *<input type="date" min={receipt?.receipt_date || ''} max={jakartaDateKey()} value={form.return_date} onChange={field('return_date')} /></label>
        <label className="form-field">Catatan dokumen<textarea rows="2" value={form.notes} onChange={field('notes')} maxLength={2000} /></label>
      </div>
      {!receipt ? <Empty>Tidak ada penerimaan dengan sisa barang yang dapat diretur.</Empty> : (
        <section className="procurement-lines">
          <div className="procurement-lines-head"><div><span className="eyebrow">Item retur</span><h3>{receipt.receipt_number} · {receipt.supplier_name}</h3></div></div>
          {receipt.items.map((row) => {
            const available = Number(row.quantity_received) - Number(row.quantity_already_returned || 0);
            if (available <= 0) return null;
            return (
              <div className="return-line" key={row.id}>
                <div className="primary-cell"><strong>{row.item_code} · {row.item_name}</strong><span>Dapat diretur {number.format(available)} {row.unit} · stok diperiksa ulang saat posting</span></div>
                <label className="form-field">Jumlah retur<input type="number" min="0" max={available} step="0.0001" value={quantities[row.id] || ''} onChange={(event) => setQuantities((current) => ({ ...current, [row.id]: event.target.value }))} placeholder="0" /></label>
                <label className="form-field">Alasan / catatan<input value={lineNotes[row.id] || ''} onChange={(event) => setLineNotes((current) => ({ ...current, [row.id]: event.target.value }))} maxLength={500} /></label>
              </div>
            );
          })}
        </section>
      )}
    </Modal>
  );
}

function Summary({ data, canApprove, canPay }) {
  const openOrders = data.purchase_orders.filter((row) => ['ORDERED', 'APPROVED', 'RECEIVED'].includes(String(row.status).toUpperCase()));
  const draftOrders = data.purchase_orders.filter((row) => row.status === 'DRAFT');
  const waitingApproval = data.purchase_orders.filter((row) => row.status === 'WAITING_APPROVAL');
  const receivedValue = data.goods_receipts.reduce((sum, row) => sum + Number(row.total_amount || 0), 0);
  const outstandingInvoices = (data.supplier_invoices || []).reduce((sum, row) => sum + Number(row.outstanding_amount || 0), 0);
  const refundOutstanding = data.supplier_returns.reduce((sum, row) => sum + Math.max(Number(row.refund_due_amount || 0) - Number(row.refunded_amount || 0), 0), 0);
  return (
    <>
      <div className="metrics-grid">
        <Metric label="Menunggu approval" value={waitingApproval.length} note={`${draftOrders.length} draft`} icon={BadgeCheck} toneName="warn" />
        <Metric label="PO terbuka" value={openOrders.length} note={`${data.suppliers.filter((row) => row.status === 'ACTIVE').length} supplier aktif`} icon={ClipboardCheck} />
        <Metric label="Penerimaan" value={data.goods_receipts.length} note={rupiah.format(receivedValue)} icon={PackageOpen} toneName="green" />
        {canApprove || canPay
          ? <Metric label="Utang supplier" value={rupiah.format(outstandingInvoices)} note={`${(data.supplier_invoices || []).filter((row) => Number(row.outstanding_amount) > 0).length} invoice · refund ditagih ${rupiah.format(refundOutstanding)}`} icon={Banknote} toneName="warn" />
          : <Metric label="Retur supplier" value={data.supplier_returns.length} note="Dokumen retur terposting" icon={RotateCcw} toneName="warn" />}
      </div>
      <div className="dashboard-grid procurement-summary-grid">
        <article className="panel panel-wide"><div className="panel-head"><div><span className="eyebrow">Purchase Order</span><h3>PO terbaru</h3></div></div>{data.purchase_orders.length ? <div className="compact-list">{data.purchase_orders.slice(0, 8).map((row) => <div className="compact-row" key={row.id}><div className="grow"><strong>{row.po_number} · {row.supplier_name}</strong><span>{dateLabel(row.po_date)} · {row.items?.length || 0} item</span></div><strong>{rupiah.format(row.total_amount)}</strong><Pill>{row.status}</Pill></div>)}</div> : <Empty>Belum ada purchase order.</Empty>}</article>
        <article className="panel"><div className="panel-head"><div><span className="eyebrow">Kontrol procurement</span><h3>Pemisahan kewenangan</h3></div></div><div className="procurement-rule"><BadgeCheck size={20} /><div><strong>Keuangan menyetujui PO</strong><span>Cetak terkunci sebelum approval.</span></div></div><div className="procurement-rule"><PackageOpen size={20} /><div><strong>Gudang memeriksa barang</strong><span>Stok bertambah saat penerimaan POSTED.</span></div></div><div className="procurement-rule"><Banknote size={20} /><div><strong>Keuangan membayar invoice</strong><span>Invoice selisih otomatis ditahan.</span></div></div></article>
      </div>
    </>
  );
}

export function ProcurementPage({ data, identity, onChanged, initialItems = [], onInitialItemsHandled }) {
  const [tab, setTab] = useState('summary');
  const [dialog, setDialog] = useState(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const canWrite = identity.permissions.has('procurement.write');
  const canApprove = identity.permissions.has('procurement.approve');
  const canReceive = identity.permissions.has('procurement.receive');
  const canPay = identity.permissions.has('procurement.pay');
  const lowered = query.trim().toLowerCase();

  useEffect(() => {
    if (!initialItems.length || !canWrite || !data.items.length) return;
    setTab('bhp_inventory');
    setDialog({ type: 'order', initialItems });
    onInitialItemsHandled?.();
  }, [initialItems, canWrite, data.items.length, onInitialItemsHandled]);

  // BHP Inventory state
  const [bhpInventory, setBhpInventory] = useState([]);
  const [bhpInvLoading, setBhpInvLoading] = useState(false);
  const [bhpInvError, setBhpInvError] = useState('');

  useEffect(() => {
    if (tab !== 'bhp_inventory') return;
    setBhpInvLoading(true);
    setBhpInvError('');
    loadBhpItemsAll()
      .then(setBhpInventory)
      .catch((e) => {
        setBhpInventory([]);
        setBhpInvError(e.message || 'Gagal memuat stok BHP per lokasi.');
      })
      .finally(() => setBhpInvLoading(false));
  }, [tab]);

  // BHP Requests state
  const [bhpRequests, setBhpRequests] = useState([]);
  const [bhpLoading, setBhpLoading] = useState(false);
  const [bhpError, setBhpError] = useState('');
  const [advanceBusy, setAdvanceBusy] = useState(false);

  useEffect(() => {
    if (tab !== 'bhp_requests') return;
    setBhpLoading(true);
    setBhpError('');
    loadBhpRequests()
      .then(setBhpRequests)
      .catch((e) => setBhpError(e.message || 'Gagal memuat permintaan stok.'))
      .finally(() => setBhpLoading(false));
  }, [tab]);

  async function advanceBhp(id, status, extra = {}) {
    setAdvanceBusy(true);
    try {
      await updateBhpRequestStatus(id, status, extra);
      const next = await loadBhpRequests();
      setBhpRequests(next);
    } catch (e) {
      setBhpError(e.message || 'Gagal update status.');
    } finally {
      setAdvanceBusy(false);
    }
  }

  const filtered = useMemo(() => {
    const source = tab === 'suppliers' ? data.suppliers
      : tab === 'orders' ? data.purchase_orders
        : tab === 'receipts' ? data.goods_receipts
          : tab === 'finance' ? (data.supplier_invoices || [])
          : tab === 'returns' ? data.supplier_returns : [];
    if (!lowered) return source;
    return source.filter((row) => Object.values(row).some((value) => typeof value === 'string' && value.toLowerCase().includes(lowered)));
  }, [data, lowered, tab]);

  async function openPoPrint(order) {
    setError('');
    setDialog({ type: 'poPrint', record: order, loading: true });
    try {
      const printData = await loadProcurementPurchaseOrderPrint(order.id);
      setDialog({ type: 'poPrint', record: order, loading: false, printData });
    } catch (nextError) {
      setDialog(null);
      setError(friendlyError(nextError));
    }
  }

  async function perform(work, message) {
    setBusy(true); setError(''); setNotice('');
    try {
      await work();
      setDialog(null);
      setNotice(message);
      await onChanged();
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setBusy(false);
    }
  }

  const visibleTabs = TABS.filter((item) => {
    if (item.key === 'summary' || item.key === 'orders' || item.key === 'bhp_requests' || item.key === 'bhp_inventory') return true;
    if (item.key === 'finance') return canApprove || canPay;
    if (item.key === 'returns') return canWrite || canReceive || canPay;
    return canWrite || canReceive;
  });

  const counts = {
    suppliers: data.suppliers.length,
    orders: data.purchase_orders.length,
    receipts: data.goods_receipts.length,
    finance: (data.supplier_invoices || []).length,
    returns: data.supplier_returns.length,
    bhp_requests: bhpRequests.filter((r) => r.status !== 'DITERIMA').length || undefined,
  };

  return (
    <div className="page-stack">
      <section className="page-heading procurement-heading">
        <div><span className="eyebrow">Procurement XV</span><h2>Pengadaan & Supplier</h2><p>PO, penerimaan, dan retur tersambung langsung ke kartu stok Supabase XV.</p></div>
        {tab !== 'summary' && <label className="search-box"><Search size={18} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cari dokumen atau supplier…" /></label>}
      </section>
      {error && (dialog
        ? <div className="floating-error">{error}</div>
        : <div className="alert alert-danger">{error}</div>)}
      {notice && <div className="alert alert-success">{notice}<button className="alert-close" onClick={() => setNotice('')}>×</button></div>}

      <div className="procurement-tabs" role="tablist">
        {visibleTabs.map((item) => <button key={item.key} className={tab === item.key ? 'active' : ''} onClick={() => { setTab(item.key); setQuery(''); }}>{item.label}{counts[item.key] !== undefined && <span>{counts[item.key]}</span>}</button>)}
      </div>

      {tab === 'summary' && <Summary data={data} canApprove={canApprove} canPay={canPay} />}
      {tab === 'suppliers' && <section className="panel table-panel"><div className="procurement-section-head"><div><span className="eyebrow">Master</span><h3>Supplier</h3></div>{canWrite && <button className="button button-primary" onClick={() => setDialog({ type: 'supplier' })}><Plus size={17} /> Supplier Baru</button>}</div>{filtered.length ? <div className="table-wrap"><table><thead><tr><th>Kode</th><th>Supplier</th><th>Kontak</th><th>Termin</th><th>Status</th><th>Aksi</th></tr></thead><tbody>{filtered.map((row) => <tr key={row.id}><td>{row.supplier_code}</td><td className="primary-cell"><strong>{row.supplier_name}</strong><span>{row.address || 'Tanpa alamat'}</span></td><td className="primary-cell"><strong>{row.contact_person || '—'}</strong><span>{row.phone || row.email || '—'}</span></td><td>{row.payment_terms_days} hari</td><td><Pill>{row.status}</Pill></td><td>{canWrite && <button className="button button-mini button-quiet" onClick={() => setDialog({ type: 'supplier', record: row })}><Pencil size={14} /> Edit</button>}</td></tr>)}</tbody></table></div> : <Empty>Supplier tidak ditemukan.</Empty>}</section>}
      {tab === 'orders' && (
        <section className="panel table-panel">
          <div className="procurement-section-head">
            <div><span className="eyebrow">Dokumen</span><h3>Purchase Order</h3></div>
            {canWrite && <button className="button button-primary" disabled={!data.suppliers.some((row) => row.status === 'ACTIVE') || !data.items.length} onClick={() => setDialog({ type: 'order' })}><Plus size={17} /> Buat PO</button>}
          </div>
          {filtered.length ? (
            <div className="table-wrap"><table><thead><tr><th>PO</th><th>Supplier</th><th>Tanggal</th><th>Item</th><th>Total</th><th>Status</th><th>Aksi</th></tr></thead><tbody>{filtered.map((row) => {
              const status = String(row.status || '').toUpperCase();
              const printable = ['APPROVED', 'ORDERED', 'RECEIVED', 'CLOSED'].includes(status);
              return <tr key={row.id}>
                <td className="primary-cell"><strong>{row.po_number}</strong><span>{statusLabel(row.fulfillment_status)}</span>{row.rejection_reason ? <span className="text-danger">Revisi: {row.rejection_reason}</span> : null}</td>
                <td>{row.supplier_name}</td><td>{dateLabel(row.po_date)}</td><td>{row.items?.length || 0}</td><td>{rupiah.format(row.total_amount)}</td><td><Pill>{row.status}</Pill></td>
                <td><div className="procurement-row-actions">
                  <button className="button button-mini button-quiet" onClick={() => openPoPrint(row)}>{printable ? <Printer size={14} /> : <FileCheck2 size={14} />} {printable ? 'Lihat / Cetak' : 'Lihat Detail'}</button>
                  {canWrite && status === 'DRAFT' ? <button className="button button-mini button-quiet" onClick={() => setDialog({ type: 'order', record: row })}><Pencil size={14} /> Edit</button> : null}
                  {canApprove && status === 'WAITING_APPROVAL' ? <button className="button button-mini button-soft" onClick={() => setDialog({ type: 'review', record: row })}><BadgeCheck size={14} /> Review</button> : null}
                  {canWrite && status === 'APPROVED' ? <button className="button button-mini button-soft" onClick={() => perform(() => markProcurementPurchaseOrderOrdered(row.id), 'PO ditandai telah dicetak/dikirim ke supplier.')}><Send size={14} /> Tandai Dikirim</button> : null}
                  {canReceive && status === 'ORDERED' ? <button className="button button-mini button-soft" onClick={() => setDialog({ type: 'receipt', record: row })}><PackageOpen size={14} /> Terima Barang</button> : null}
                </div></td>
              </tr>;
            })}</tbody></table></div>
          ) : <Empty>Purchase order tidak ditemukan.</Empty>}
        </section>
      )}
      {tab === 'receipts' && (
        <section className="panel table-panel">
          <div className="procurement-section-head"><div><span className="eyebrow">Stok masuk</span><h3>Goods Receipt & Pemeriksaan Invoice</h3></div>{canReceive && <button className="button button-primary" onClick={() => setDialog({ type: 'receipt' })}><Plus size={17} /> Posting Penerimaan</button>}</div>
          {filtered.length ? <div className="table-wrap"><table><thead><tr><th>Penerimaan</th><th>PO / Supplier</th><th>Tanggal</th><th>Item</th><th>Nilai Barang</th><th>Invoice</th><th>Status</th><th>Aksi</th></tr></thead><tbody>{filtered.map((row) => {
            const invoice = (data.supplier_invoices || []).find((item) => item.goods_receipt_id === row.id);
            return <tr key={row.id}>
              <td className="primary-cell"><strong>{row.receipt_number}</strong><span>{row.supplier_delivery_number || 'Tanpa nomor surat jalan'}</span></td>
              <td className="primary-cell"><strong>{row.po_number || '—'}</strong><span>{row.supplier_name}</span></td>
              <td>{dateLabel(row.receipt_date)}</td><td>{row.items?.length || 0}</td><td>{rupiah.format(row.total_amount)}</td>
              <td>{invoice ? <div className="primary-cell"><strong>{invoice.invoice_number}</strong><span>{rupiah.format(invoice.invoice_amount)}</span></div> : <span className="muted">Belum dicatat</span>}</td>
              <td>{invoice ? <><Pill>{invoice.match_status}</Pill> <Pill>{invoice.status}</Pill></> : <Pill>{row.status}</Pill>}</td>
              <td>{canReceive && !invoice ? <button className="button button-mini button-soft" onClick={() => setDialog({ type: 'invoice', record: row })}><FileCheck2 size={14} /> Catat Invoice</button> : '—'}</td>
            </tr>;
          })}</tbody></table></div> : <Empty>Belum ada penerimaan barang.</Empty>}
        </section>
      )}
      {tab === 'finance' && (
        <section className="panel table-panel">
          <div className="procurement-section-head"><div><span className="eyebrow">Keuangan supplier</span><h3>Invoice & Pembayaran</h3></div></div>
          {filtered.length ? <div className="table-wrap"><table><thead><tr><th>Invoice</th><th>Supplier</th><th>PO / Penerimaan</th><th>Jatuh Tempo</th><th>Nilai</th><th>Retur</th><th>Dibayar</th><th>Status</th><th>Aksi</th></tr></thead><tbody>{filtered.map((row) => <tr key={row.id}>
            <td className="primary-cell"><strong>{row.invoice_number}</strong><span>{dateLabel(row.invoice_date)}</span></td>
            <td>{row.supplier_name}</td><td className="primary-cell"><strong>{row.po_number}</strong><span>{row.receipt_number}</span></td><td>{dateLabel(row.due_date)}</td>
            <td className="primary-cell"><strong>{rupiah.format(row.invoice_amount)}</strong><span>{row.match_status === 'VARIANCE' ? `Selisih ${rupiah.format(row.variance_amount)}` : 'Sesuai penerimaan'}</span></td>
            <td className="primary-cell"><strong>{rupiah.format(row.credit_amount)}</strong><span>Pengurang utang</span></td>
            <td className="primary-cell"><strong>{rupiah.format(row.paid_amount)}</strong><span>Sisa {rupiah.format(row.outstanding_amount)}</span></td>
            <td><Pill>{row.match_status}</Pill> <Pill>{row.status}</Pill></td>
            <td>{canPay && row.match_status === 'MATCHED' && Number(row.outstanding_amount) > 0 ? <button className="button button-mini button-primary" onClick={() => setDialog({ type: 'payment', record: row })}><Banknote size={14} /> Bayar</button> : row.match_status === 'VARIANCE' ? <span className="text-warning">Ditahan</span> : '—'}</td>
          </tr>)}</tbody></table></div> : <Empty>Belum ada invoice supplier.</Empty>}
        </section>
      )}
      {tab === 'returns' && (
        <section className="panel table-panel">
          <div className="procurement-section-head"><div><span className="eyebrow">Stok & keuangan</span><h3>Retur Supplier</h3></div>{canReceive && <button className="button button-primary" onClick={() => setDialog({ type: 'return' })}><Plus size={17} /> Posting Retur</button>}</div>
          {filtered.length ? <div className="table-wrap"><table><thead><tr><th>Retur</th><th>Penerimaan</th><th>Supplier</th><th>Tanggal</th><th>Item</th><th>Total</th><th>Pengurang Utang</th><th>Refund</th><th>Status Keuangan</th><th>Aksi</th></tr></thead><tbody>{filtered.map((row) => {
            const refundOutstanding = Math.max(Number(row.refund_due_amount || 0) - Number(row.refunded_amount || 0), 0);
            return <tr key={row.id}>
              <td className="primary-cell"><strong>{row.return_number}</strong><span><Pill>{row.status}</Pill></span></td>
              <td>{row.receipt_number || '—'}</td><td>{row.supplier_name}</td><td>{dateLabel(row.return_date)}</td><td>{row.items?.length || 0}</td><td>{rupiah.format(row.total_amount)}</td>
              <td>{rupiah.format(row.applied_to_invoice_amount)}</td>
              <td className="primary-cell"><strong>{rupiah.format(row.refunded_amount)}</strong><span>Sisa {rupiah.format(refundOutstanding)}</span></td>
              <td><Pill>{row.financial_status}</Pill></td>
              <td>{canPay && refundOutstanding > 0 ? <button className="button button-mini button-primary" onClick={() => setDialog({ type: 'refund', record: row })}><Banknote size={14} /> Terima Refund</button> : '—'}</td>
            </tr>;
          })}</tbody></table></div> : <Empty>Belum ada retur supplier.</Empty>}
        </section>
      )}

      {tab === 'bhp_requests' && (
        <section className="panel table-panel">
          <div className="procurement-section-head">
            <div><span className="eyebrow">Internal Divisi</span><h3>Permintaan Internal BHP</h3></div>
            <button className="button button-quiet" onClick={() => {
              setBhpLoading(true);
              loadBhpRequests().then(setBhpRequests).catch(() => {}).finally(() => setBhpLoading(false));
            }}>↻ Refresh</button>
          </div>

          {/* Pipeline summary */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            {['REQUESTED','DIPROSES','DIKIRIM','DITERIMA'].map((s) => {
              const c = bhpRequests.filter((r) => r.status === s).length;
              const st = BHP_STATUS_COLOR[s] || {};
              const labels = { REQUESTED: 'Diminta', DIPROSES: 'Diproses', DIKIRIM: 'Dikirim', DITERIMA: 'Diterima' };
              return (
                <div key={s} style={{ background: st.bg, border: `1px solid ${st.border}`, borderRadius: 10, padding: '10px 18px', minWidth: 100 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: st.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>{labels[s]}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: st.color }}>{c}</div>
                </div>
              );
            })}
          </div>

          {bhpError && <div className="alert alert-danger">{bhpError}</div>}
          {bhpLoading ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Memuat…</div>
          ) : bhpRequests.length === 0 ? (
            <Empty>Belum ada permintaan stok dari klinik.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th>Item</th><th>Diminta</th><th>Tgl</th><th>Status</th><th>Catatan</th><th>Aksi</th>
                </tr></thead>
                <tbody>
                  {bhpRequests.map((r) => {
                    const st = BHP_STATUS_COLOR[r.status] || {};
                    return (
                      <tr key={r.id}>
                        <td className="primary-cell">
                          <strong>{r.item_name || '—'}</strong>
                          <span>{r.requested_by || ''}</span>
                        </td>
                        <td>{r.requested_qty} {r.unit || ''}</td>
                        <td>{dateLabel(r.requested_at)}</td>
                        <td>
                          <span style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}`, borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
                            {r.status}
                          </span>
                        </td>
                        <td style={{ fontSize: 13, color: '#6b7280', maxWidth: 180 }}>{r.notes || '—'}</td>
                        <td>
                          {r.status === 'REQUESTED' && (
                            <button className="button button-mini button-primary" disabled={advanceBusy}
                              onClick={() => advanceBhp(r.id, 'DIPROSES')}>
                              ⚙ Proses
                            </button>
                          )}
                          {r.status === 'DIPROSES' && (
                            <button className="button button-mini button-primary" disabled={advanceBusy}
                              onClick={() => advanceBhp(r.id, 'DIKIRIM')}>
                              <Truck size={13} /> Kirim
                            </button>
                          )}
                          {r.status === 'DIKIRIM' && (
                            <span style={{ color: '#7c3aed', fontSize: 12, fontStyle: 'italic' }}>Menunggu konfirmasi klinik</span>
                          )}
                          {r.status === 'DITERIMA' && (
                            <span style={{ color: '#15803d', fontSize: 13 }}>✓ Diterima ({r.received_qty} {r.unit || ''})</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {tab === 'bhp_inventory' && (
        <section className="panel table-panel">
          <div className="procurement-section-head">
            <div><span className="eyebrow">Referensi</span><h3>Inventaris BHP Klinik</h3></div>
            <button className="button button-quiet" onClick={() => {
              setBhpInvLoading(true);
              setBhpInvError('');
              loadBhpItemsAll()
                .then(setBhpInventory)
                .catch((e) => {
                  setBhpInventory([]);
                  setBhpInvError(e.message || 'Gagal memuat stok BHP per lokasi.');
                })
                .finally(() => setBhpInvLoading(false));
            }}>↻ Refresh</button>
          </div>
          {bhpInvError ? (
            <div className="alert alert-error" style={{ margin: '0 20px 20px' }}>
              <strong>Stok gagal dimuat.</strong> {bhpInvError}
            </div>
          ) : bhpInvLoading ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Memuat…</div>
          ) : bhpInventory.length === 0 ? (
            <Empty>Belum ada item BHP.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th>Kode</th><th>Nama Item</th><th>Divisi</th><th>Satuan</th>
                  <th style={{ textAlign: 'right' }}>Stok Gudang</th>
                  <th style={{ textAlign: 'right' }}>Wellness</th>
                  <th style={{ textAlign: 'right' }}>Estetik</th>
                  <th style={{ textAlign: 'right' }}>Dental</th>
                  <th style={{ textAlign: 'right' }}>Dokter Umum</th>
                  <th style={{ textAlign: 'right' }}>Apotik</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'right' }}>Min</th>
                  <th style={{ textAlign: 'right' }}>Target</th>
                  <th>Status</th>
                  <th>Aksi</th>
                </tr></thead>
                <tbody>
                  {bhpInventory.map((item) => {
                    const gudangStock = Number(item.gudang_stock || 0);
                    const totalStock = Number(item.current_stock || 0);
                    const minimumStock = Number(item.minimum_stock || 0);
                    const isHabis = gudangStock <= 0;
                    const isLimit = !isHabis && gudangStock <= minimumStock;
                    const shouldPurchase = minimumStock > 0 && gudangStock <= minimumStock;
                    return (
                      <tr key={item.id}>
                        <td style={{ fontSize: 12, color: '#9ca3af' }}>{item.item_code}</td>
                        <td className="primary-cell"><strong>{item.name}</strong><span>{item.category || ''}</span></td>
                        <td style={{ fontSize: 13 }}>{item.division || 'Semua'}</td>
                        <td style={{ fontSize: 13 }}>{item.unit || '—'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: isHabis ? '#dc2626' : isLimit ? '#d97706' : '#111' }}>
                          {gudangStock.toLocaleString('id-ID')}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: Number(item.wellness_stock || 0) <= 0 ? '#dc2626' : '#111' }}>
                          {Number(item.wellness_stock || 0).toLocaleString('id-ID')}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: Number(item.estetik_stock || 0) <= 0 ? '#dc2626' : '#111' }}>
                          {Number(item.estetik_stock || 0).toLocaleString('id-ID')}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: Number(item.dental_stock || 0) <= 0 ? '#dc2626' : '#111' }}>
                          {Number(item.dental_stock || 0).toLocaleString('id-ID')}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: Number(item.dokter_umum_stock || 0) <= 0 ? '#dc2626' : '#111' }}>
                          {Number(item.dokter_umum_stock || 0).toLocaleString('id-ID')}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: Number(item.apotik_stock || 0) <= 0 ? '#dc2626' : '#111' }}>
                          {Number(item.apotik_stock || 0).toLocaleString('id-ID')}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>
                          {totalStock.toLocaleString('id-ID')}
                        </td>
                        <td style={{ textAlign: 'right', fontSize: 13, color: '#6b7280' }}>{item.minimum_stock ?? '—'}</td>
                        <td style={{ textAlign: 'right', fontSize: 13, color: '#6b7280' }}>{item.target_stock ?? '—'}</td>
                        <td>
                          {isHabis ? (
                            <span style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 5, fontSize: 11, fontWeight: 700, padding: '2px 8px' }}>HABIS</span>
                          ) : isLimit ? (
                            <span style={{ background: '#fffbeb', color: '#d97706', borderRadius: 5, fontSize: 11, fontWeight: 700, padding: '2px 8px' }}>LIMIT</span>
                          ) : (
                            <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: 5, fontSize: 11, fontWeight: 700, padding: '2px 8px' }}>OK</span>
                          )}
                        </td>
                        <td>
                          {canWrite && shouldPurchase ? (
                            <button className="button button-mini button-primary" type="button" onClick={() => setDialog({ type: 'order', initialItem: item })}>
                              <Plus size={13} /> Purchase
                            </button>
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: 12 }}>-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {dialog?.type === 'supplier' && <SupplierForm supplier={dialog.record} busy={busy} onClose={() => setDialog(null)} onSaved={(supplier) => perform(() => saveProcurementSupplier(supplier), 'Supplier berhasil disimpan.')} />}
      {dialog?.type === 'order' && <PurchaseOrderForm order={dialog.record} initialItem={dialog.initialItem} initialItems={dialog.initialItems} suppliers={data.suppliers} items={data.items} busy={busy} onClose={() => setDialog(null)} onSaved={(order, items, submit) => perform(() => saveProcurementPurchaseOrder(order, items, submit), submit ? 'Purchase order diajukan ke Keuangan dan menunggu persetujuan.' : 'Draft purchase order tersimpan.')} />}
      {dialog?.type === 'poPrint' && dialog.loading && <Modal title="Purchase Order" subtitle="Menyiapkan dokumen cetak…" icon={ClipboardCheck} onClose={() => setDialog(null)} actions={<button className="button button-quiet" onClick={() => setDialog(null)}>Tutup</button>}><div className="content-loading"><div className="loader" /><span>Memuat detail PO…</span></div></Modal>}
      {dialog?.type === 'poPrint' && !dialog.loading && <PurchaseOrderPrintModal data={dialog.printData} canApprove={canApprove} canOrder={canWrite} canReceive={canReceive} onClose={() => setDialog(null)} onReview={() => setDialog({ type: 'review', record: dialog.record })} onOrder={() => perform(() => markProcurementPurchaseOrderOrdered(dialog.record.id), 'PO ditandai telah dicetak/dikirim ke supplier.')} onReceive={() => setDialog({ type: 'receipt', record: dialog.record })} />}
      {dialog?.type === 'review' && <PurchaseOrderReviewModal order={dialog.record} busy={busy} onClose={() => setDialog(null)} onDecision={(action, reason) => perform(() => reviewProcurementPurchaseOrder(dialog.record.id, action, reason), action === 'APPROVE' ? 'Purchase order disetujui. Dokumen sudah dapat dicetak.' : 'Purchase order dikembalikan ke Inventory untuk revisi.')} />}
      {dialog?.type === 'receipt' && <GoodsReceiptForm orders={data.purchase_orders} initialOrder={dialog.record} busy={busy} onClose={() => setDialog(null)} onSaved={(receipt, items) => perform(() => postProcurementGoodsReceipt(receipt, items), 'Penerimaan dan invoice berhasil diposting. Stok telah bertambah; invoice cocok dapat diproses Keuangan.')} />}
      {dialog?.type === 'invoice' && <SupplierInvoiceForm receipt={dialog.record} busy={busy} onClose={() => setDialog(null)} onSaved={(receiptId, invoice) => perform(() => recordProcurementSupplierInvoice(receiptId, invoice), 'Invoice supplier berhasil dicatat dan diperiksa.')} />}
      {dialog?.type === 'payment' && <SupplierPaymentForm invoice={dialog.record} busy={busy} onClose={() => setDialog(null)} onSaved={(invoiceId, payment) => perform(() => payProcurementSupplierInvoice(invoiceId, payment), 'Pembayaran supplier berhasil diposting setelah memperhitungkan retur.')} />}
      {dialog?.type === 'return' && <SupplierReturnForm receipts={data.goods_receipts} busy={busy} onClose={() => setDialog(null)} onSaved={(supplierReturn, items) => perform(() => postProcurementSupplierReturn(supplierReturn, items), 'Retur berhasil diposting. Stok berkurang dan nilai retur otomatis diperhitungkan pada invoice supplier.')} />}
      {dialog?.type === 'refund' && <SupplierRefundForm supplierReturn={dialog.record} busy={busy} onClose={() => setDialog(null)} onSaved={(returnId, refund) => perform(() => receiveProcurementSupplierRefund(returnId, refund), 'Refund supplier berhasil diterima dan dicatat oleh Keuangan.')} />}
    </div>
  );
}
